import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import {
  extendSortingDataAccessor,
  ObservableDataSource,
  timestampSortingAccessor,
} from '@principle-theorem/ng-shared';
import { LabJobIncomeTimestampField } from '@principle-theorem/principle-core/interfaces';
import {
  type IPractitionerLabFeeGrouping,
  type IPractitionerLabFeeReportRecord,
} from '@principle-theorem/reporting';
import {
  DAY_MONTH_YEAR_FORMAT,
  type ITimePeriod,
} from '@principle-theorem/shared';
import { sum } from 'lodash';
import { type Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { PractitionerLabFeesDrilldownToCSV } from './practitioner-lab-fees-drilldown-to-csv';

const TIMESTAMP_FIELD_LABEL: Record<LabJobIncomeTimestampField, string> = {
  [LabJobIncomeTimestampField.SentAt]: 'Date Sent',
  [LabJobIncomeTimestampField.ReceivedAt]: 'Date Received',
  [LabJobIncomeTimestampField.IssuedAt]: 'Date Issued',
};

@Component({
  selector: 'pr-practitioner-lab-fees-drilldown',
  templateUrl: './practitioner-lab-fees-drilldown.component.html',
  styleUrls: ['./practitioner-lab-fees-drilldown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PractitionerLabFeesDrilldownComponent implements OnDestroy {
  description = 'Lab job costs attributed to this practitioner.';
  labFees$ = new ReplaySubject<IPractitionerLabFeeGrouping>(1);
  dateRange$ = new ReplaySubject<ITimePeriod>(1);
  dataSource: ObservableDataSource<IPractitionerLabFeeReportRecord>;
  csvData$: Observable<IPractitionerLabFeeReportRecord[]>;
  dateFormat = DAY_MONTH_YEAR_FORMAT;
  displayedColumns = ['labJobDate', 'patient', 'lab', 'type', 'cost'];
  total$: Observable<number>;
  csvTranslator = new PractitionerLabFeesDrilldownToCSV();
  labJobTimestampField: LabJobIncomeTimestampField =
    LabJobIncomeTimestampField.IssuedAt;

  get labJobDateLabel(): string {
    return TIMESTAMP_FIELD_LABEL[this.labJobTimestampField];
  }

  @Input()
  set labFees(labFees: IPractitionerLabFeeGrouping) {
    if (labFees) {
      this.labFees$.next(labFees);
    }
  }

  @Input()
  set dateRange(dateRange: ITimePeriod) {
    if (dateRange) {
      this.dateRange$.next(dateRange);
    }
  }

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('labJobTimestampField')
  set setLabJobTimestampField(field: LabJobIncomeTimestampField | undefined) {
    if (field) {
      this.labJobTimestampField = field;
      this.csvTranslator = new PractitionerLabFeesDrilldownToCSV(field);
    }
  }

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  constructor() {
    const data$ = this.labFees$.pipe(map((labFees) => labFees.records));

    this.csvData$ = data$;
    this.dataSource = new ObservableDataSource(data$);
    this.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data: IPractitionerLabFeeReportRecord, sortHeaderId: string) => {
        switch (sortHeaderId) {
          case 'labJobDate':
            return timestampSortingAccessor(
              data.labJob[this.labJobTimestampField]
            );
          case 'cost':
            return data.summary.labFeesAmount;
          default:
            return;
        }
      }
    );

    this.total$ = data$.pipe(
      map((records) =>
        sum(records.map((record) => record.summary.labFeesAmount))
      )
    );
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
  }
}
